
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface CheckoutErrorProps {
  errorMessage: string;
  errorDetails?: string | null;
  debugInfo?: Record<string, any> | null;
  onRetry: () => void;
  isRetrying: boolean;
}

const CheckoutError: React.FC<CheckoutErrorProps> = ({ 
  errorMessage, 
  errorDetails,
  debugInfo,
  onRetry, 
  isRetrying 
}) => {
  const navigate = useNavigate();
  const [showDebug, setShowDebug] = useState(false);

  return (
    <>
      <Alert variant="destructive" className="mb-6 text-left">
        <AlertCircle className="h-4 w-4 mr-2" />
        <AlertTitle>Checkout Error</AlertTitle>
        <AlertDescription>
          {errorMessage}
          {errorDetails && (
            <details className="mt-2 text-sm">
              <summary className="cursor-pointer font-medium">Technical details</summary>
              <pre className="mt-2 p-2 bg-red-100 rounded overflow-x-auto text-xs">
                {errorDetails}
              </pre>
            </details>
          )}
        </AlertDescription>
      </Alert>
      
      <div className="space-y-4">
        <Button 
          className="w-full" 
          size="lg" 
          onClick={onRetry}
          disabled={isRetrying}
        >
          {isRetrying ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Retrying...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </>
          )}
        </Button>
        
        <Button 
          variant="outline" 
          className="w-full" 
          size="lg" 
          onClick={() => navigate('/pricing')}
          disabled={isRetrying}
        >
          Return to Plans
        </Button>

        {debugInfo && (
          <div className="mt-4">
            <Button
              variant="ghost"
              className="w-full text-sm"
              onClick={() => setShowDebug(!showDebug)}
            >
              {showDebug ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Hide Debug Information
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Show Debug Information
                </>
              )}
            </Button>
            
            {showDebug && (
              <div className="mt-2 border border-gray-200 rounded p-3">
                <h3 className="text-sm font-medium mb-2">Debug Information</h3>
                <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto">
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default CheckoutError;
